import React, { useState } from 'react';
import { X, ChevronRight, SlidersHorizontal } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';

/**
 * Multilevel Filter Drawer Component
 * Opens from right side with expandable filter categories
 */
const FilterDrawer = ({ isOpen, onClose, filters, onApplyFilters, activeFiltersCount }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [rangeValues, setRangeValues] = useState({});
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  // Handle checkbox toggle
  const handleCheckboxChange = (categoryId, filterId) => {
    setSelectedFilters(prev => {
      const categoryFilters = prev[categoryId] || [];
      const isSelected = categoryFilters.includes(filterId);
      
      const newFilters = {
        ...prev,
        [categoryId]: isSelected 
          ? categoryFilters.filter(id => id !== filterId)
          : [...categoryFilters, filterId]
      };

      // If this is a category filter, update subcategory options
      if (categoryId === 'category') {
        const categoryFilter = filters.find(f => f.id === 'category');
        const selectedCategories = newFilters[categoryId] || [];
        
        if (categoryFilter?.categoryMap && selectedCategories.length > 0) {
          // Get all subcategories for selected main categories
          const allSubcategories = new Set();
          selectedCategories.forEach(cat => {
            const subs = categoryFilter.categoryMap[cat] || [];
            subs.forEach(sub => allSubcategories.add(sub));
          });
          
          setSubcategoryOptions(Array.from(allSubcategories).map(sub => ({
            id: sub,
            label: sub,
            count: 0 // Count will be calculated properly later
          })));
        } else {
          setSubcategoryOptions([]);
          // Clear subcategory selection if no category selected
          newFilters.subcategory = [];
        }
      }
      
      return newFilters;
    });
  };

  // Handle range input change
  const handleRangeChange = (categoryId, type, value) => {
    setRangeValues(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        [type]: value
      }
    }));
  };

  // Apply filters
  const handleApply = () => {
    onApplyFilters({ selectedFilters, rangeValues });
    onClose();
  };

  // Reset all filters
  const handleReset = () => {
    setSelectedFilters({});
    setRangeValues({});
    onApplyFilters({ selectedFilters: {}, rangeValues: {} });
  };

  // Count active filters in a category
  const getActiveCategoryCount = (categoryId) => {
    const checkboxCount = (selectedFilters[categoryId] || []).length;
    const rangeCount = rangeValues[categoryId] ? 
      (rangeValues[categoryId].min || rangeValues[categoryId].max ? 1 : 0) : 0;
    return checkboxCount + rangeCount;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5 text-gray-700" />
            <h2 className="text-lg font-semibold">Filters</h2>
            {activeFiltersCount > 0 && (
              <Badge className="bg-blue-600 text-white">{activeFiltersCount}</Badge>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Filter Categories */}
        <div className="flex-1 overflow-y-auto">
          {filters.map((category) => {
            const activeCount = getActiveCategoryCount(category.id);
            const isExpanded = expandedCategory === category.id;

            return (
              <div key={category.id} className="border-b">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{category.label}</span>
                    {activeCount > 0 && (
                      <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                        {activeCount}
                      </Badge>
                    )}
                  </div>
                  <ChevronRight 
                    className={`h-5 w-5 text-gray-500 transition-transform ${
                      isExpanded ? 'rotate-90' : ''
                    }`}
                  />
                </button>

                {/* Category Content */}
                {isExpanded && (
                  <div className="px-4 pb-4 bg-gray-50 space-y-3">
                    {/* Note for subcategory dependency */}
                    {category.id === 'subcategory' && subcategoryOptions.length === 0 && (
                      <p className="text-sm text-gray-500 italic">
                        Select a category first to see subcategories
                      </p>
                    )}
                    
                    {/* Checkbox Options */}
                    {category.type === 'checkbox' && (category.id === 'subcategory' ? subcategoryOptions : category.options).map((option) => (
                      <div key={option.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`${category.id}-${option.id}`}
                          checked={(selectedFilters[category.id] || []).includes(option.id)}
                          onCheckedChange={() => handleCheckboxChange(category.id, option.id)}
                        />
                        <Label 
                          htmlFor={`${category.id}-${option.id}`}
                          className="text-sm font-normal cursor-pointer flex items-center justify-between w-full"
                        >
                          <span>{option.label}</span>
                          {option.count !== undefined && (
                            <span className="text-gray-500 text-xs">({option.count})</span>
                          )}
                        </Label>
                      </div>
                    ))}

                    {/* Range Input */}
                    {category.type === 'range' && (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs text-gray-600 mb-1 block">Min</Label>
                            <Input
                              type="number"
                              placeholder={category.minPlaceholder || "Min"}
                              value={rangeValues[category.id]?.min || ''}
                              onChange={(e) => handleRangeChange(category.id, 'min', e.target.value)}
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-600 mb-1 block">Max</Label>
                            <Input
                              type="number"
                              placeholder={category.maxPlaceholder || "Max"}
                              value={rangeValues[category.id]?.max || ''}
                              onChange={(e) => handleRangeChange(category.id, 'max', e.target.value)}
                              className="h-8"
                            />
                          </div>
                        </div>
                        {category.unit && (
                          <p className="text-xs text-gray-500">Values in {category.unit}</p>
                        )}
                      </div>
                    )}

                    {/* Text Input */}
                    {category.type === 'text' && (
                      <Input
                        type="text"
                        placeholder={category.placeholder || "Enter value"}
                        value={rangeValues[category.id]?.value || ''}
                        onChange={(e) => handleRangeChange(category.id, 'value', e.target.value)}
                        className="h-8"
                      />
                    )}

                    {/* Slider Input */}
                    {category.type === 'slider' && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span>{rangeValues[category.id]?.min || category.min || 0}</span>
                          <span>{rangeValues[category.id]?.max || category.max || 100}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs text-gray-600 mb-1 block">Min</Label>
                            <Input
                              type="number"
                              min={category.min || 0}
                              max={category.max || 100}
                              placeholder={category.minPlaceholder || "Min"}
                              value={rangeValues[category.id]?.min || ''}
                              onChange={(e) => handleRangeChange(category.id, 'min', e.target.value)}
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-600 mb-1 block">Max</Label>
                            <Input
                              type="number"
                              min={category.min || 0}
                              max={category.max || 100}
                              placeholder={category.maxPlaceholder || "Max"}
                              value={rangeValues[category.id]?.max || ''}
                              onChange={(e) => handleRangeChange(category.id, 'max', e.target.value)}
                              className="h-8"
                            />
                          </div>
                        </div>
                        {category.unit && (
                          <p className="text-xs text-gray-500">{category.unit}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t bg-white space-y-2">
          <Button 
            onClick={handleApply} 
            className="w-full"
          >
            Apply Filters
          </Button>
          <Button 
            onClick={handleReset} 
            variant="outline" 
            className="w-full"
          >
            Reset All
          </Button>
        </div>
      </div>
    </>
  );
};

export default FilterDrawer;
